<template>
  <div class="page-container">
    <el-card class="search-card">
      <el-row :gutter="20">
        <el-col :span="6">
          <el-input 
            v-model="searchKeyword" 
            placeholder="搜索角色名称或权限字符串" 
            clearable
            @clear="handleSearch"
            @keyup.enter.native="handleSearch">
            <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
          </el-input>
        </el-col>
        <el-col :span="4">
          <el-button type="primary" @click="handleAdd" icon="el-icon-plus">新增角色</el-button>
        </el-col>
      </el-row>
    </el-card>

    <el-card>
      <el-table :data="tableData" border v-loading="loading">
        <el-table-column prop="id" label="ID" width="80"></el-table-column>
        <el-table-column prop="roleName" label="角色名称" width="150"></el-table-column>
        <el-table-column prop="roleKey" label="权限字符串" width="150"></el-table-column>
        <el-table-column label="数据权限" width="150">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.dataScope === 0" type="danger">全部数据（超级）</el-tag>
            <el-tag v-else-if="scope.row.dataScope === 1" type="warning">全部数据（管理）</el-tag>
            <el-tag v-else type="info">仅本人数据</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="sort" label="排序" width="80"></el-table-column>
        <el-table-column label="状态" width="100">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.status === 0" type="success">正常</el-tag>
            <el-tag v-else type="danger">停用</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="description" label="描述" show-overflow-tooltip></el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="180">
          <template slot-scope="scope">
            {{ formatDate(scope.row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="260" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click="handleAssignPermission(scope.row)">分配权限</el-button>
            <el-button size="mini" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button 
              size="mini" 
              type="danger" 
              @click="handleDelete(scope.row)"
              :disabled="scope.row.id < 10">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        @current-change="handlePageChange"
        @size-change="handleSizeChange"
        :current-page="page.current"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="page.size"
        layout="total, sizes, prev, pager, next, jumper"
        :total="page.total"
        style="margin-top: 20px; text-align: right;">
      </el-pagination>
    </el-card>

    <!-- 编辑对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="600px" :close-on-click-modal="false">
      <el-form :model="form" :rules="rules" ref="roleForm" label-width="120px">
        <el-form-item label="角色名称" prop="roleName">
          <el-input v-model="form.roleName" placeholder="请输入角色名称"></el-input>
        </el-form-item>
        <el-form-item label="权限字符串" prop="roleKey">
          <el-input v-model="form.roleKey" placeholder="请输入权限字符串，如：admin"></el-input>
          <div class="tip">权限字符串用于权限判断，建议使用小写英文和下划线</div>
        </el-form-item>
        <el-form-item label="数据权限" prop="dataScope">
          <el-select v-model="form.dataScope" placeholder="请选择数据权限范围" style="width: 100%;">
            <el-option :value="0" label="全部数据（超级管理员）">
              <span style="float: left">全部数据（超级管理员）</span>
              <span style="float: right; color: #8492a6; font-size: 13px">无限制查看所有数据</span>
            </el-option>
            <el-option :value="1" label="全部数据（管理员）">
              <span style="float: left">全部数据（管理员）</span>
              <span style="float: right; color: #8492a6; font-size: 13px">可查看所有数据</span>
            </el-option>
            <el-option :value="2" label="仅本人数据">
              <span style="float: left">仅本人数据</span>
              <span style="float: right; color: #8492a6; font-size: 13px">只能查看自己创建的数据</span>
            </el-option>
          </el-select>
          <div class="tip">设置该角色可以查看的数据范围</div>
        </el-form-item>
        <el-form-item label="显示顺序" prop="sort">
          <el-input-number v-model="form.sort" :min="0" :max="999"></el-input-number>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="form.status">
            <el-radio :label="0">正常</el-radio>
            <el-radio :label="1">停用</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="描述">
          <el-input type="textarea" v-model="form.description" :rows="3" placeholder="请输入角色描述"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSave" :loading="saving">保存</el-button>
      </span>
    </el-dialog>

    <!-- 分配权限对话框 -->
    <el-dialog 
      title="分配权限" 
      :visible.sync="permissionDialogVisible" 
      width="800px"
      top="5vh"
      :close-on-click-modal="false">
      <div class="permission-dialog-content">
        <!-- 工具栏 -->
        <div class="permission-toolbar">
          <div class="toolbar-left">
            <el-button-group>
              <el-button 
                size="small" 
                icon="el-icon-check"
                @click="checkAll">
                全选
              </el-button>
              <el-button 
                size="small" 
                icon="el-icon-close"
                @click="uncheckAll">
                取消全选
              </el-button>
            </el-button-group>
            <el-button-group style="margin-left: 10px;">
              <el-button 
                size="small" 
                icon="el-icon-arrow-down"
                @click="expandAll">
                展开
              </el-button>
              <el-button 
                size="small" 
                icon="el-icon-arrow-up"
                @click="collapseAll">
                收起
              </el-button>
            </el-button-group>
          </div>
          <div class="toolbar-right">
            <el-tag type="info">已选: {{ checkedCount }}</el-tag>
          </div>
        </div>

        <!-- 搜索框 -->
        <div class="permission-search">
          <el-input 
            v-model="permissionSearchKeyword"
            placeholder="搜索权限名称或标识"
            size="small"
            clearable
            prefix-icon="el-icon-search">
          </el-input>
        </div>

        <!-- 权限树 -->
        <div class="permission-tree-wrapper">
          <el-tree
            ref="permissionTree"
            :data="filteredPermissionTree"
            show-checkbox
            node-key="id"
            :props="{ children: 'children', label: 'label' }"
            :default-checked-keys="checkedPermissions"
            :default-expand-all="isPermissionExpandAll"
            :filter-node-method="filterPermissionNode"
            @check="handlePermissionCheck">
            <span class="custom-tree-node" slot-scope="{ node, data }">
              <span>
                <i :class="data.icon" v-if="data.icon" style="margin-right: 5px;"></i>
                <span>{{ data.label }}</span>
                <el-tag 
                  size="mini" 
                  type="warning" 
                  v-if="data.permissionType === 2"
                  style="margin-left: 8px;">
                  按钮
                </el-tag>
              </span>
              <span class="node-key">{{ data.permissionKey }}</span>
            </span>
          </el-tree>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="permissionDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSavePermissions" :loading="savingPermissions">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'RoleManage',
  data() {
    return {
      tableData: [],
      loading: false,
      searchKeyword: '',
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      dialogVisible: false,
      dialogTitle: '',
      form: {
        roleName: '',
        roleKey: '',
        dataScope: 2,
        sort: 0,
        status: 0,
        description: ''
      },
      saving: false,
      rules: {
        roleName: [
          { required: true, message: '请输入角色名称', trigger: 'blur' },
          { min: 2, max: 50, message: '角色名称长度在 2 到 50 个字符', trigger: 'blur' }
        ],
        roleKey: [
          { required: true, message: '请输入权限字符串', trigger: 'blur' },
          { pattern: /^[a-z_]+$/, message: '权限字符串只能包含小写字母和下划线', trigger: 'blur' }
        ]
      },
      permissionDialogVisible: false,
      permissionTree: [],
      checkedPermissions: [],
      currentRoleId: null,
      savingPermissions: false,
      isPermissionExpandAll: true,
      permissionSearchKeyword: '',
      checkedCount: 0
    }
  },
  computed: {
    // 过滤后的权限树
    filteredPermissionTree() {
      if (!this.permissionSearchKeyword) {
        return this.permissionTree
      }
      return this.filterTree(this.permissionTree, this.permissionSearchKeyword)
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const res = await this.$axios.get('/admin/role/page', {
          params: {
            current: this.page.current,
            size: this.page.size,
            keyword: this.searchKeyword
          }
        })
        if (res.data.code === 200) {
          this.tableData = res.data.data.records
          this.page.total = res.data.data.total
        }
      } catch (error) {
        this.$message.error('加载失败')
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page.current = 1
      this.loadData()
    },
    handlePageChange(page) {
      this.page.current = page
      this.loadData()
    },
    handleSizeChange(size) {
      this.page.size = size
      this.page.current = 1
      this.loadData()
    },
    handleAdd() {
      this.dialogTitle = '新增角色'
      this.form = {
        roleName: '',
        roleKey: '',
        dataScope: 2,
        sort: 0,
        status: 0,
        description: ''
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.roleForm && this.$refs.roleForm.clearValidate()
      })
    },
    handleEdit(row) {
      this.dialogTitle = '编辑角色'
      this.form = { ...row }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.roleForm && this.$refs.roleForm.clearValidate()
      })
    },
    async handleSave() {
      this.$refs.roleForm.validate(async (valid) => {
        if (!valid) return

        this.saving = true
        try {
          if (this.form.id) {
            await this.$axios.put(`/admin/role/${this.form.id}`, this.form)
          } else {
            await this.$axios.post('/admin/role', this.form)
          }
          this.$message.success('保存成功')
          this.dialogVisible = false
          this.loadData()
        } catch (error) {
          this.$message.error(error.response?.data?.message || '保存失败')
        } finally {
          this.saving = false
        }
      })
    },
    handleDelete(row) {
      if (row.id < 10) {
        this.$message.warning('系统预置角色不允许删除')
        return
      }
      
      this.$confirm(`确定删除角色"${row.roleName}"?`, '提示', {
        type: 'warning'
      }).then(async () => {
        try {
          await this.$axios.delete(`/admin/role/${row.id}`)
          this.$message.success('删除成功')
          this.loadData()
        } catch (error) {
          this.$message.error('删除失败')
        }
      }).catch(() => {})
    },
    async handleAssignPermission(row) {
      this.currentRoleId = row.id
      this.permissionDialogVisible = true
      
      // 加载权限树
      try {
        const treeRes = await this.$axios.get('/admin/role/permissions/tree')
        if (treeRes.data.code === 200) {
          this.permissionTree = treeRes.data.data
        }
        
        // 加载角色已有权限
        const permRes = await this.$axios.get(`/admin/role/${row.id}/permissions`)
        if (permRes.data.code === 200) {
          this.checkedPermissions = permRes.data.data
        }
      } catch (error) {
        this.$message.error('加载权限数据失败')
      }
    },
    handlePermissionCheck() {
      // 更新已选权限数量
      this.$nextTick(() => {
        const checkedKeys = this.$refs.permissionTree.getCheckedKeys()
        const halfCheckedKeys = this.$refs.permissionTree.getHalfCheckedKeys()
        this.checkedCount = checkedKeys.length + halfCheckedKeys.length
      })
    },
    async handleSavePermissions() {
      const checkedKeys = this.$refs.permissionTree.getCheckedKeys()
      const halfCheckedKeys = this.$refs.permissionTree.getHalfCheckedKeys()
      const allKeys = [...checkedKeys, ...halfCheckedKeys]
      
      this.savingPermissions = true
      try {
        await this.$axios.put(`/admin/role/${this.currentRoleId}/permissions`, {
          permissionIds: allKeys
        })
        this.$message.success('权限分配成功')
        this.permissionDialogVisible = false
      } catch (error) {
        this.$message.error('权限分配失败')
      } finally {
        this.savingPermissions = false
      }
    },

    // 全选权限
    checkAll() {
      this.setAllChecked(this.permissionTree, true)
    },

    // 取消全选
    uncheckAll() {
      this.$refs.permissionTree.setCheckedKeys([])
      this.checkedCount = 0
    },

    // 递归设置所有节点选中状态
    setAllChecked(nodes, checked) {
      nodes.forEach(node => {
        this.$refs.permissionTree.setChecked(node.id, checked, false)
        if (node.children && node.children.length > 0) {
          this.setAllChecked(node.children, checked)
        }
      })
      this.handlePermissionCheck()
    },

    // 展开所有节点
    expandAll() {
      this.isPermissionExpandAll = true
      this.toggleExpandAll(this.permissionTree, true)
    },

    // 收起所有节点
    collapseAll() {
      this.isPermissionExpandAll = false
      this.toggleExpandAll(this.permissionTree, false)
    },

    // 切换展开状态
    toggleExpandAll(nodes, isExpand) {
      nodes.forEach(node => {
        const treeNode = this.$refs.permissionTree.getNode(node.id)
        if (treeNode) {
          treeNode.expanded = isExpand
        }
        if (node.children && node.children.length > 0) {
          this.toggleExpandAll(node.children, isExpand)
        }
      })
    },

    // 过滤权限节点
    filterPermissionNode(value, data) {
      if (!value) return true
      const keyword = value.toLowerCase()
      return data.label.toLowerCase().includes(keyword) || 
             (data.permissionKey && data.permissionKey.toLowerCase().includes(keyword))
    },

    // 递归过滤树
    filterTree(tree, keyword) {
      const filtered = []
      tree.forEach(node => {
        const nodeMatch = this.filterPermissionNode(keyword, node)
        const children = node.children ? this.filterTree(node.children, keyword) : []
        
        if (nodeMatch || children.length > 0) {
          filtered.push({
            ...node,
            children: children.length > 0 ? children : node.children
          })
        }
      })
      return filtered
    },
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleString('zh-CN')
    }
  }
}
</script>

<style scoped>
.page-container {
  padding: 20px;
  background: transparent;
  min-height: calc(100vh - 60px);
}

.search-card {
  margin-bottom: 20px;
}

.tip {
  margin-top: 5px;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

.permission-dialog-content {
  max-height: 70vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.permission-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #ebeef5;
}

.toolbar-left {
  display: flex;
  align-items: center;
}

.toolbar-right {
  display: flex;
  align-items: center;
}

.permission-search {
  margin-bottom: 15px;
}

.permission-tree-wrapper {
  flex: 1;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 10px;
  max-height: 50vh;
}

.custom-tree-node {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  padding-right: 8px;
}

.node-key {
  font-size: 12px;
  color: #909399;
  margin-left: 10px;
}
</style>
